"use client";

import { PropsWithChildren } from "react";

import { cn } from "@app/utils";

import { useFormContext } from "./Form";
import { Label } from "./Label";

interface FormItemProps extends PropsWithChildren {
  label?: string | React.ReactNode;
  icon?: JSX.Element | React.ComponentType<any>; // JSX.Element for passing an Icon directly, React.ComponentType<any> for a function or JSX that gets rendered in the icon slot
  labelOnly?: boolean;
  iconHref?: string;
  className?: string;
  border?: boolean;
  readOnly?: boolean;
  htmlFor?: string;
  flexDisplay?: boolean;
  padding?: string | object;
  fullWidthLabel?: boolean;
}

export function FormItem({
  label,
  icon: Icon,
  children,
  iconHref,
  className,
  labelOnly = false,
  border = false,
  readOnly = false,
  htmlFor,
  flexDisplay = false,
  padding = "",
  fullWidthLabel = false,
}: FormItemProps) {
  const { isEditing } = useFormContext();

  if (labelOnly)
    return (
      <div className={cn("w-full", className)}>
        <Label label={label} htmlFor={htmlFor} />
        {children}
      </div>
    );

  const inputWrapperClassName = cn(
    "text-sm w-full disabled:bg-gray-200 disabled:border-gray-400",
    {
      "flex justify-between": flexDisplay,
      "border-none": !border,
      "border border-solid border-gray-500 p-xs rounded-xs hover:border-color-primary has-[:focus]:border-color-primary":
        isEditing && border,
    },
    padding
  );

  return (
    <div className={cn("FormItem__wrapper w-full flex items-start", className)}>
      {label !== null || label !== undefined ? (
        <div
          className={cn(
            "FormItem__label-wrapper min-w-[160px] w-[160px] pr-4 flex items-start mb-xs",
            { "w-full": fullWidthLabel }
          )}
        >
          <Label label={label} htmlFor={htmlFor} className="leading-[20px]" />
        </div>
      ) : null}
      <div
        className={`FormItem__input-wrapper ${inputWrapperClassName} ${readOnly && isEditing && border ? "bg-gray-100" : ""}`}
      >
        {children}
        {iconHref && !isEditing ? (
          <a href={iconHref} target="_blank" rel="noreferrer">
            {" "}
            {Icon && !isEditing ? (
              typeof Icon === "function" ? (
                <Icon />
              ) : (
                Icon
              )
            ) : null}
          </a>
        ) : (
          <>
            {Icon && !isEditing ? (
              typeof Icon === "function" ? (
                <Icon />
              ) : (
                Icon
              )
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
